// src/employees/EmployeeMobileTimecard.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';

function EmployeeMobileTimecard() {
  const { id } = useParams();

  const [isClockedIn, setIsClockedIn] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [laps, setLaps] = useState([]);
  const [currentLapStart, setCurrentLapStart] = useState(null);
  const [jobSite, setJobSite] = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [finalNotes, setFinalNotes] = useState('');

  // Lunch state
  const [lunchStarted, setLunchStarted] = useState(false);
  const [lunchStartTime, setLunchStartTime] = useState(null);
  const [lunchElapsed, setLunchElapsed] = useState(0);

  // Format duration (stopwatch style)
  const formatDuration = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours ? hours + 'h ' : ''}${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
  };

  // Format actual clock time
  const formatClockTime = (timestamp) => {
    return format(new Date(timestamp), 'h:mm a');
  };

  // Main shift timer
  useEffect(() => {
    let interval;
    if (isClockedIn) {
      interval = setInterval(() => {
        setElapsed(Date.now() - startTime);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isClockedIn, startTime]);

  // Lunch timer
  useEffect(() => {
    let lunchInterval;
    if (lunchStarted && lunchStartTime) {
      lunchInterval = setInterval(() => {
        setLunchElapsed(Date.now() - lunchStartTime);
      }, 1000);
    }
    return () => clearInterval(lunchInterval);
  }, [lunchStarted, lunchStartTime]);

  const handleClockIn = () => {
    if (!jobSite) return alert('Please select a job site first');
    setIsClockedIn(true);
    setStartTime(Date.now());
    setCurrentLapStart(Date.now());
    setLaps([]);
    setLunchStarted(false);
    setLunchStartTime(null);
    setLunchElapsed(0);
    setFinalNotes('');
  };

  const handleClockOut = () => {
    setIsClockedIn(false);
    setShowSummary(true);
  };

  const confirmClockOut = () => {
    // In real app: send to backend
    alert(`Clock out confirmed!\n\nTotal time: ${formatDuration(elapsed)}\n\nJob Site: ${jobSite}\nNotes: ${finalNotes || 'None'}`);
    
    // Reset
    setIsClockedIn(false);
    setStartTime(null);
    setElapsed(0);
    setLaps([]);
    setCurrentLapStart(null);
    setShowSummary(false);
    setFinalNotes('');
    setLunchStarted(false);
    setLunchStartTime(null);
    setLunchElapsed(0);
  };

  const startLunch = () => {
    if (!isClockedIn || lunchStarted) return;
    setLunchStarted(true);
    setLunchStartTime(Date.now());
    setLunchElapsed(0);
    addLap('Start Lunch');
  };

  const endLunch = () => {
    if (!lunchStarted) return;
    const now = Date.now();
    addLap('End Lunch');
    setLunchStarted(false);
    setLunchStartTime(null);
    setLunchElapsed(0);
  };

  const addLap = (label = 'Timestamp') => {
    if (!currentLapStart) return;
    const now = Date.now();
    const lapDuration = now - currentLapStart;
    setLaps([...laps, { 
      timestamp: now,           // exact real-world time
      duration: lapDuration,    // stopwatch duration
      label,
      note: ''
    }]);
    setCurrentLapStart(now);
  };

  const updateLapNote = (index, note) => {
    const updated = [...laps];
    updated[index].note = note;
    setLaps(updated);
  };

  if (showSummary) {
    return (
      <div className="min-h-screen bg-gray-950 text-zinc-100 flex flex-col items-center justify-center p-6">
        <div className="bg-gray-900 rounded-3xl p-8 w-full max-w-md shadow-2xl border border-gray-800">
          <h1 className="text-3xl font-bold text-center mb-6">Shift Summary</h1>

          <div className="text-center mb-8">
            <p className="text-6xl font-mono font-bold text-indigo-400">
              {formatDuration(elapsed)}
            </p>
            <p className="text-zinc-400 mt-2 text-lg">Total Time</p>
          </div>

          <div className="space-y-4 mb-8">
            {laps.map((lap, idx) => (
              <div key={idx} className="flex flex-col bg-gray-800 p-4 rounded-xl">
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <p className="font-medium text-lg">{lap.label}</p>
                    <p className="text-sm text-zinc-400">
                      {formatClockTime(lap.timestamp)} • {formatDuration(lap.duration)}
                    </p>
                  </div>
                </div>
                <input
                  type="text"
                  value={lap.note}
                  onChange={e => updateLapNote(idx, e.target.value)}
                  placeholder="Add note..."
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-zinc-500"
                />
              </div>
            ))}
          </div>

          <textarea
            value={finalNotes}
            onChange={e => setFinalNotes(e.target.value)}
            placeholder="Any additional notes for this shift..."
            className="w-full h-24 px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-zinc-500 resize-none mb-6"
          />

          <div className="flex gap-4">
            <button
              onClick={() => setShowSummary(false)}
              className="flex-1 py-4 bg-gray-700 hover:bg-gray-600 rounded-xl font-medium transition-colors"
            >
              Back to Shift
            </button>
            <button
              onClick={confirmClockOut}
              className="flex-1 py-4 bg-green-700 hover:bg-green-600 rounded-xl font-medium transition-colors"
            >
              Confirm & Clock Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-zinc-100 flex flex-col p-6 space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Timecard</h1>
        <p className="text-zinc-400 text-sm">ID: {id}</p>
      </div>

      {/* Job Site + Geofencing */}
      <div className="space-y-3">
        <label className="block text-sm text-zinc-400">Job Site</label>
        <select
          value={jobSite}
          onChange={e => setJobSite(e.target.value)}
          className="w-full px-4 py-3.5 bg-gray-800 border border-gray-700 rounded-2xl text-white text-lg"
          disabled={isClockedIn}
        >
          <option value="">Select Job Site</option>
          <option value="project1">DMV Facility Upgrade</option>
          <option value="project2">Residential Rewire</option>
          <option value="project3">Office Retrofit</option>
        </select>

        {isClockedIn && !jobSite && (
          <p className="text-red-400 text-sm mt-2">
            ⚠️ You are outside the geofencing of the jobsite. Clock actions may be restricted.
          </p>
        )}
      </div>

      {/* Main Timer */}
      <div className="flex flex-col items-center justify-center flex-1 py-10">
        <div className="text-8xl sm:text-9xl font-mono font-extrabold tracking-tight text-indigo-400 mb-3">
          {formatDuration(elapsed)}
        </div>
        <p className="text-xl text-zinc-400 mb-10">Shift Time</p>

        {/* Lunch Timer */}
        {lunchStarted && lunchStartTime && (
          <div className="text-center mb-10">
            <div className="text-6xl font-mono font-bold text-red-400">
              {formatDuration(lunchElapsed)}
            </div>
            <p className="text-red-300 text-lg mt-2">Lunch Break</p>
          </div>
        )}

        {/* Clock Buttons */}
        {!isClockedIn ? (
          <button
            onClick={handleClockIn}
            disabled={!jobSite}
            className="w-full max-w-md py-8 text-3xl font-bold bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 disabled:from-green-900 disabled:to-green-800 disabled:cursor-not-allowed rounded-3xl shadow-2xl transition-all transform hover:scale-[1.02]"
          >
            Clock In
          </button>
        ) : (
          <button
            onClick={handleClockOut}
            className="w-full max-w-md py-8 text-3xl font-bold bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 rounded-3xl shadow-2xl transition-all transform hover:scale-[1.02]"
          >
            Clock Out
          </button>
        )}
      </div>

      {/* Lunch & Timestamp Controls */}
      {isClockedIn && (
        <div className="grid grid-cols-2 gap-4 mb-10">
          <button
            onClick={startLunch}
            disabled={lunchStarted}
            className={`py-6 rounded-2xl font-bold text-xl transition-all ${
              lunchStarted
                ? 'bg-amber-900 text-amber-300 cursor-not-allowed'
                : 'bg-amber-700 hover:bg-amber-600 text-white shadow-lg'
            }`}
          >
            {lunchStarted ? 'Lunch Started' : 'Start Lunch'}
          </button>

          <button
            onClick={endLunch}
            disabled={!lunchStarted}
            className={`py-6 rounded-2xl font-bold text-xl transition-all ${
              !lunchStarted
                ? 'bg-gray-800 text-zinc-500 cursor-not-allowed'
                : 'bg-amber-700 hover:bg-amber-600 text-white shadow-lg'
            }`}
          >
            End Lunch
          </button>

          <button
            onClick={() => addLap('Timestamp')}
            className="col-span-2 py-6 bg-indigo-700 hover:bg-indigo-600 rounded-2xl font-bold text-xl text-white shadow-lg transition-all"
          >
            Take Timestamp
          </button>
        </div>
      )}

      {/* Lap / Timestamp History */}
      {isClockedIn && laps.length > 0 && (
        <div className="bg-gray-900 rounded-3xl border border-gray-800 p-6 shadow-2xl">
          <h3 className="text-xl font-bold mb-5">Today's Activity</h3>
          <div className="space-y-4">
            {laps.map((lap, idx) => (
              <div key={idx} className="bg-gray-800 p-5 rounded-2xl">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <p className="font-bold text-lg">{lap.label}</p>
                    <p className="text-sm text-zinc-400">
                      {formatClockTime(lap.timestamp)} • {formatDuration(lap.duration)}
                    </p>
                  </div>
                </div>
                <input
                  type="text"
                  value={lap.note}
                  onChange={e => {
                    const updated = [...laps];
                    updated[idx].note = e.target.value;
                    setLaps(updated);
                  }}
                  placeholder="Add note..."
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white text-base placeholder-zinc-500"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default EmployeeMobileTimecard;